import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { translateSign } from '../../../utils/zodiacTranslations';

import { useNavigate } from 'react-router-dom';
import { localizedValue, localizedServerContent } from '../../../localization/presentationLanguage';

const synText = (es, en) => localizedValue({ es, en });

const DIMENSION_LABELS = {
  energy: () => synText('Energía', 'Energy'),
  expression: () => synText('Expresión', 'Expression'),
  magnetism: () => synText('Magnetismo', 'Magnetism'),
  stability: () => synText('Estabilidad', 'Stability'),
  growth: () => synText('Crecimiento', 'Growth'),
};
const dimensionLabel = (key) => DIMENSION_LABELS[key]?.() || key;

const SIGN_META = {
  aries: { label: 'Aries', emoji: '♈', element: 'Fuego' },
  taurus: { label: 'Tauro', emoji: '♉', element: 'Tierra' },
  gemini: { label: 'Géminis', emoji: '♊', element: 'Aire' },
  cancer: { label: 'Cáncer', emoji: '♋', element: 'Agua' },
  leo: { label: 'Leo', emoji: '♌', element: 'Fuego' },
  virgo: { label: 'Virgo', emoji: '♍', element: 'Tierra' },
  libra: { label: 'Libra', emoji: '♎', element: 'Aire' },
  scorpio: { label: 'Escorpio', emoji: '♏', element: 'Agua' },
  sagittarius: { label: 'Sagitario', emoji: '♐', element: 'Fuego' },
  capricorn: { label: 'Capricornio', emoji: '♑', element: 'Tierra' },
  aquarius: { label: 'Acuario', emoji: '♒', element: 'Aire' },
  pisces: { label: 'Piscis', emoji: '♓', element: 'Agua' },
};

const styles = {
  resultCard: {
    borderRadius: 28,
    padding: 22,
    border: '1px solid rgba(218,165,32,0.20)',
    background:
      'linear-gradient(180deg, rgba(20,10,34,0.97) 0%, rgba(11,8,20,0.98) 100%)',
    boxShadow: '0 20px 60px rgba(0,0,0,0.34)',
    position: 'relative',
    overflow: 'hidden',
  },
  desktopTop: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1.15fr) minmax(280px,0.85fr)',
    gap: 18,
    marginBottom: 20,
    alignItems: 'stretch',
  },
  mobileTop: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
    marginBottom: 20,
  },
  pairCard: {
    borderRadius: 22,
    padding: 18,
    border: '1px solid rgba(218,165,32,0.18)',
    background: 'rgba(255,255,255,0.03)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100%',
  },
  pairHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 14,
    justifySelf: 'center',
  },
  signPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 16,
    background: 'rgba(218,165,32,0.08)',
    border: '1px solid rgba(218,165,32,0.16)',
    minWidth: 150,
  },
  signGlyph: {
    fontSize: 22,
    color: '#F7D774',
    width: 24,
    textAlign: 'center',
  },
  signName: {
    fontWeight: 900,
    color: '#FFF3C4',
    fontSize: 15,
    lineHeight: 1.1,
  },
  signMeta: {
    color: 'rgba(255,243,196,0.62)',
    fontSize: 12,
    marginTop: 2,
  },
  heart: {
    alignSelf: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 52,
    height: 52,
    borderRadius: 16,
    color: '#F7D774',
    fontSize: 22,
    background: 'linear-gradient(180deg, rgba(218,165,32,0.18), rgba(218,165,32,0.06))',
    border: '1px solid rgba(218,165,32,0.20)',
    boxShadow: '0 0 20px rgba(218,165,32,0.10)',
  },
  mobileHeart: {
    justifySelf: 'center',
  },
  summaryText: {
    margin: 0,
    color: 'rgba(255,243,196,0.75)',
    fontSize: 14,
    lineHeight: 1.65,
    textAlign: 'center',
  },
  scoreCard: {
    borderRadius: 22,
    padding: 20,
    border: '1px solid rgba(218,165,32,0.18)',
    background:
      'radial-gradient(circle at top, rgba(218,165,32,0.14), transparent 42%), rgba(255,255,255,0.03)',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 14px 36px rgba(0,0,0,0.18)',
  },
  scoreOrbWrap: {
    position: 'relative',
    width: '100%',
    height: 170,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 18,
    marginBottom: 14,
  },

  scoreOrbRing: {
    position: 'absolute',
    width: 130,
    height: 130,
    borderRadius: '50%',
    border: '2px solid rgba(218,165,32,0.35)',
    boxShadow:
      '0 0 18px rgba(218,165,32,0.25), inset 0 0 18px rgba(218,165,32,0.15)',
    animation: 'synOrbPulse 3.2s ease-in-out infinite',
  },
  scoreLabel: {
    margin: 0,
    color: 'rgba(255,243,196,0.72)',
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  scoreValue: {
    position: 'relative',
    zIndex: 2,
    margin: 0,
    fontSize: 'clamp(40px, 6vw, 64px)',
    lineHeight: 1,
    fontWeight: 900,
    color: '#F7D774',
    textShadow: '0 0 24px rgba(218,165,32,0.18)',
    letterSpacing: '-0.03em',
  },
  archetypeBox: {
    marginTop: 10,
    padding: '12px 14px',
    borderRadius: 16,
    background: 'rgba(75,0,130,0.30)',
    border: '1px solid rgba(218,165,32,0.14)',
  },
  archetypeTitle: {
    margin: 0,
    fontWeight: 900,
    color: '#FFF3C4',
    fontSize: 15,
  },
  archetypeSubtitle: {
    margin: '6px 0 0',
    fontSize: 13,
    color: 'rgba(255,243,196,0.72)',
    lineHeight: 1.45,
  },
  bottomGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0,1fr)',
    gap: 18,
  },
  cardBlock: {
    borderRadius: 22,
    padding: 18,
    border: '1px solid rgba(218,165,32,0.16)',
    background: 'rgba(255,255,255,0.025)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
  },
  dimensionsCard: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 26,
    padding: 20,
    border: '1px solid rgba(247,215,116,0.22)',
    background:
      'linear-gradient(135deg, rgba(75,0,130,0.22) 0%, rgba(14,10,26,0.96) 52%, rgba(218,165,32,0.10) 100%)',
    boxShadow:
      '0 18px 46px rgba(0,0,0,0.24), 0 0 20px rgba(138,43,226,0.10), inset 0 1px 0 rgba(255,255,255,0.05)',
    display: 'grid',
    gap: 14,
  },
  dimensionsGlowA: {
    position: 'absolute',
    top: -34,
    left: -26,
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(138,43,226,0.18) 0%, rgba(138,43,226,0) 72%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
  },
  dimensionsGlowB: {
    position: 'absolute',
    right: -30,
    bottom: -36,
    width: 160,
    height: 160,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(247,215,116,0.14) 0%, rgba(247,215,116,0) 72%)',
    filter: 'blur(12px)',
    pointerEvents: 'none',
  },
  dimensionsHeader: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gap: 6,
  },
  dimensionsEyebrow: {
    margin: 0,
    color: '#F7D774',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  dimensionsTitle: {
    margin: 0,
    color: '#FFF3C4',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.15,
    textShadow: '0 0 14px rgba(247,215,116,0.12)',
  },
  dimensionsList: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gap: 10,
  },
  premiumBarCard: {
    borderRadius: 18,
    padding: '12px 12px 11px',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  premiumBarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 8,
  },
  premiumBarLabelWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  premiumBarDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#F7D774',
    boxShadow: '0 0 12px rgba(247,215,116,0.30)',
    flex: '0 0 auto',
  },
  premiumBarLabel: {
    fontWeight: 900,
    color: '#FFF3C4',
    fontSize: 14,
    lineHeight: 1.1,
  },
  premiumBarValue: {
    fontWeight: 900,
    color: '#F7D774',
    fontSize: 14,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  premiumBarTrack: {
    height: 12,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.07)',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.04)',
    position: 'relative',
  },
  premiumBarFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #4B0082 0%, #8A2BE2 38%, #DAA520 100%)',
    boxShadow: '0 0 18px rgba(218,165,32,0.18)',
    transition: 'width 850ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease',
    willChange: 'width',
  },
  interpretiveBlock: {
  borderRadius: 24,
  padding: 20,
  border: '1px solid rgba(247,215,116,0.24)',
  background:
    'linear-gradient(135deg, rgba(75,0,130,0.26) 0%, rgba(18,10,34,0.96) 55%, rgba(218,165,32,0.12) 100%)',
  boxShadow:
    '0 18px 44px rgba(0,0,0,0.24), 0 0 22px rgba(138,43,226,0.12), inset 0 1px 0 rgba(255,255,255,0.05)',
  position: 'relative',
  overflow: 'hidden',
},

heroCard: {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: 30,
  padding: '26px 22px 22px',
  border: '1px solid rgba(247,215,116,0.28)',
  background:
    'linear-gradient(135deg, rgba(75,0,130,0.32) 0%, rgba(22,12,40,0.96) 52%, rgba(218,165,32,0.14) 100%)',
  boxShadow:
    '0 28px 74px rgba(0,0,0,0.34), 0 0 28px rgba(138,43,226,0.14), inset 0 1px 0 rgba(255,255,255,0.06)',
  display: 'grid',
  gap: 18,
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
},
heroGlowA: {
  position: 'absolute',
  top: -42,
  left: -34,
  width: 170,
  height: 170,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(138,43,226,0.26) 0%, rgba(138,43,226,0) 72%)',
  filter: 'blur(12px)',
  pointerEvents: 'none',
},
heroGlowB: {
  position: 'absolute',
  right: -38,
  bottom: -46,
  width: 190,
  height: 190,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(247,215,116,0.20) 0%, rgba(247,215,116,0) 72%)',
  filter: 'blur(14px)',
  pointerEvents: 'none',
},
heroTopRow: {
  position: 'relative',
  zIndex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 14,
  alignItems: 'stretch',
},
heroSignCol: {
  minWidth: 0,
  borderRadius: 22,
  padding: '18px 12px 14px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.028) 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
  display: 'grid',
  justifyItems: 'center',
  gap: 6,
  textAlign: 'center',
},
heroGlyph: {
  fontSize: 38,
  lineHeight: 1,
  color: '#F7D774',
  textShadow: '0 0 18px rgba(247,215,116,0.26)',
},
heroSignName: {
  fontSize: 24,
  lineHeight: 1.05,
  fontWeight: 900,
  color: '#FFF3C4',
  textShadow: '0 0 14px rgba(247,215,116,0.12)',
},
heroElementRow: {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  minHeight: 30,
  padding: '0 12px',
  borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(255,255,255,0.06)',
  color: 'rgba(255,243,196,0.84)',
  fontSize: 12,
  fontWeight: 800,
  letterSpacing: 0.35,
},
heroBottomBlock: {
  position: 'relative',
  zIndex: 1,
  borderRadius: 20,
  padding: '16px 16px 15px',
  border: '1px solid rgba(247,215,116,0.16)',
  background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  display: 'grid',
  gap: 8,
},
heroBadge: {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'fit-content',
  minHeight: 30,
  padding: '0 12px',
  borderRadius: 999,
  border: '1px solid rgba(247,215,116,0.18)',
  background: 'linear-gradient(180deg, rgba(247,215,116,0.12) 0%, rgba(255,255,255,0.03) 100%)',
  color: '#FFF3C4',
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
},
heroSummary: {
  margin: 0,
  color: 'rgba(255,243,196,0.92)',
  fontSize: 14,
  lineHeight: 1.75,
},
  interpretiveGlowA: {
    position: 'absolute',
    top: -36,
    left: -28,
    width: 140,
    height: 140,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(138,43,226,0.22) 0%, rgba(138,43,226,0) 72%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
  },
  interpretiveGlowB: {
    position: 'absolute',
    right: -32,
    bottom: -42,
    width: 170,
    height: 170,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(247,215,116,0.16) 0%, rgba(247,215,116,0) 72%)',
    filter: 'blur(12px)',
    pointerEvents: 'none',
  },
  interpretiveHeader: {
    position: 'relative',
    zIndex: 1,
    display: 'grid',
    gap: 6,
    marginBottom: 10,
  },
  interpretiveEyebrow: {
    margin: 0,
    color: '#F7D774',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  interpretiveTitle: {
    margin: 0,
    color: '#FFF3C4',
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.15,
    textShadow: '0 0 14px rgba(247,215,116,0.14)',
  },
  interpretiveText: {
    position: 'relative',
    zIndex: 1,
    margin: 0,
    color: 'rgba(255,243,196,0.90)',
    lineHeight: 1.8,
    fontSize: 15,
  },
  sectionTitle: {
    margin: '0 0 12px',
    fontSize: 15,
    fontWeight: 900,
    color: '#FFF3C4',
  },
  barRow: {
    marginBottom: 12,
  },
  barHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 6,
    fontSize: 14,
  },
  barLabel: {
    fontWeight: 800,
    color: '#FFF3C4',
  },
  barValue: {
    fontWeight: 900,
    color: '#F7D774',
  },
  barTrack: {
    height: 10,
    borderRadius: 999,
    background: 'rgba(255,255,255,0.07)',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.04)',
    position: 'relative',
  },
  barFill: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #4B0082 0%, #8A2BE2 35%, #DAA520 100%)',
    boxShadow: '0 0 18px rgba(218,165,32,0.15)',
    transition: 'width 850ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease',
    willChange: 'width',
  },
  narrativeText: {
    margin: 0,
    color: 'rgba(255,243,196,0.88)',
    lineHeight: 1.7,
    fontSize: 15,
  },
  disclosureCard: {
    borderRadius: 18,
    padding: '14px 16px',
    border: '1px solid rgba(218,165,32,0.14)',
    background: 'rgba(218,165,32,0.06)',
    color: 'rgba(255,243,196,0.78)',
    fontSize: 13,
    lineHeight: 1.65,
    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
  },
  shimmer: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.03) 35%, rgba(255,255,255,0.09) 50%, rgba(255,255,255,0.03) 65%, transparent 100%)',
    transform: 'translateX(-120%)',
    animation: 'synShimmer 2.8s ease-in-out infinite',
    pointerEvents: 'none',
  },
  scoreSubcopy: {
    margin: '6px 0 0',
    color: 'rgba(255,243,196,0.66)',
    fontSize: 12,
    lineHeight: 1.5,
  },
  revealBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 8,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(218,165,32,0.10)',
    border: '1px solid rgba(218,165,32,0.14)',
    color: 'rgba(255,243,196,0.86)',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.25,
    textTransform: 'uppercase',
  },
  scoreGlow: {
    position: 'absolute',
    width: 160,
    height: 160,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(218,165,32,0.16) 0%, rgba(218,165,32,0.06) 42%, transparent 72%)',
    top: -48,
    right: -36,
    filter: 'blur(10px)',
    pointerEvents: 'none',
  },
  pairReveal: {
    animation: 'synFadeUp 520ms cubic-bezier(0.22, 1, 0.36, 1)',
  },
  narrativeReveal: {
    animation: 'synFadeUp 620ms cubic-bezier(0.22, 1, 0.36, 1)',
  },
  dimReveal: {
    animation: 'synFadeUp 560ms cubic-bezier(0.22, 1, 0.36, 1)',
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  actionButton: {
    minHeight: 54,
    border: '1px solid rgba(247,215,116,0.36)',
    borderRadius: 16,
    padding: '0 16px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(247,215,116,0.10) 42%, rgba(138,43,226,0.10) 100%)',
    color: '#FFF3C4',
    fontWeight: 900,
    fontSize: 13,
    letterSpacing: 0.25,
    cursor: 'pointer',
    boxShadow: '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)',
    transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
  },
  actionButtonDisabled: {
    opacity: 0.58,
    cursor: 'not-allowed',
  },
  secondaryActionButton: {
    minHeight: 54,
    border: '1px solid rgba(247,215,116,0.42)',
    borderRadius: 16,
    padding: '0 16px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(247,215,116,0.10) 45%, rgba(138,43,226,0.12) 100%)',
    color: '#FFF3C4',
    fontWeight: 900,
    fontSize: 13,
    letterSpacing: 0.25,
    cursor: 'pointer',
    boxShadow: '0 18px 44px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
    transition: 'transform 160ms ease, box-shadow 160ms ease, filter 160ms ease',
  },
  sharePanel: {
    marginTop: 12,
    padding: 14,
    borderRadius: 18,
    border: '1px solid rgba(247,215,116,0.18)',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02)), rgba(0,0,0,0.20)',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 10,
    boxShadow: '0 14px 36px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)',
  },
  shareStatus: {
    marginTop: 10,
    color: '#FFF3C4',
    fontSize: 13,
    lineHeight: 1.5,
  },
};

const QUICK_RESULT_CARD_STYLE_ID = 'zodiacus-quick-result-card-keyframes';
const QUICK_RESULT_CARD_STYLE_TEXT = `
  @keyframes synFadeUp {
    0% { opacity: 0; transform: translateY(14px) scale(0.985); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes synShimmer {
    0% { transform: translateX(-120%); }
    55% { transform: translateX(120%); }
    100% { transform: translateX(120%); }
  }

  @keyframes synOrbPulse {
    0% { transform: scale(0.9); opacity: 0.6; }
    50% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(0.9); opacity: 0.6; }
  }
`;

function metaForSign(sign) {
  return SIGN_META[sign] || { label: sign, emoji: '✦', element: '—' };
}

function labelForSign(sign) {
  return SIGN_META[sign]?.label || sign;
}

function translateSignsInText(text) {
  const raw = String(text || '');
  if (!raw) return '';

  return raw.replace(
    /\b(aries|taurus|gemini|cancer|leo|virgo|libra|scorpio|sagittarius|capricorn|aquarius|pisces)\b/gi,
    (match) => translateSign(match)
  );
}

function adaptElementPhrase(text, signA, signB) {
  const raw = String(text || '');
  if (!raw) return raw;

  const element = metaForSign(signA)?.element || '';
  const elementLower = element.toLowerCase();

  // Replace generic elemental phrases with the correct one
  return raw
    .replace(/el mismo fuego/gi, `La misma ${elementLower}`)
    .replace(/la misma fuego/gi, `La misma ${elementLower}`)
    .replace(/mismo fuego/gi, `${elementLower}`);
}

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function buildRadarPolygonPoints(data = [], cx = 180, cy = 180, radius = 118) {
  if (!Array.isArray(data) || !data.length) return '';

  return data
    .map((item, index) => {
      const angle = (360 / data.length) * index;
      const value = Math.max(0, Math.min(100, Number(item?.value || 0)));
      const point = polarToCartesian(cx, cy, (value / 100) * radius, angle);
      return `${point.x},${point.y}`;
    })
    .join(' ');
}

function buildRadarAxes(data = [], cx = 180, cy = 180, radius = 118) {
  if (!Array.isArray(data)) return [];

  return data.map((item, index) => {
    const angle = (360 / Math.max(1, data.length)) * index;
    const end = polarToCartesian(cx, cy, radius, angle);
    const label = polarToCartesian(cx, cy, radius + 24, angle);
    return {
      key: item?.metric || `axis-${index}`,
      metric: item?.metric || '',
      end,
      label,
    };
  });
}

function buildRadarRingPoints(totalAxes, ringRadius, cx = 180, cy = 180) {
  if (!totalAxes) return '';

  return Array.from({ length: totalAxes }, (_, index) => {
    const angle = (360 / totalAxes) * index;
    const point = polarToCartesian(cx, cy, ringRadius, angle);
    return `${point.x},${point.y}`;
  }).join(' ');
}

function wrapCanvasText(ctx, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [];
  let current = words[0];

  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) current = candidate;
    else {
      lines.push(current);
      current = words[i];
    }
  }

  lines.push(current);
  return lines;
}

function truncateCanvasText(ctx, text, maxWidth) {
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (ctx.measureText(raw).width <= maxWidth) return raw;

  let result = raw;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function drawRoundedRect(ctx, x, y, width, height, radius) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

async function canvasToBlob(canvas) {
  return await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

function downloadBlobFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function openSharePopup(url, name = 'share-window') {
  const width = 720;
  const height = 760;
  const left = Math.max(0, Math.round((window.screen.width - width) / 2));
  const top = Math.max(0, Math.round((window.screen.height - height) / 2));
  window.open(
    url,
    name,
    `popup=yes,width=${width},height=${height},left=${left},top=${top},noopener,noreferrer`
  );
}

function buildQuickShareSummary(result) {
  const signA = labelForSign(result?.pair?.signA);
  const signB = labelForSign(result?.pair?.signB);
  const score = Number(result?.compatibility?.masterScore || 0);
  const dims = result?.compatibility?.dimensions || {};
  const archetype = localizedServerContent(result?.archetype?.label, 'Solar connection');

  return [
    synText('✦ Compatibilidad Solar · Zodiacus', '✦ Solar Compatibility · Zodiacus'),
    `${signA} vs ${signB}`,
    `${synText('Compatibilidad global', 'Overall compatibility')}: ${score}%`,
    `${synText('Arquetipo', 'Archetype')}: ${archetype}`,
    `${dimensionLabel('energy')}: ${Number(dims.energy || 0)}%`,
    `${dimensionLabel('expression')}: ${Number(dims.expression || 0)}%`,
    `${dimensionLabel('magnetism')}: ${Number(dims.magnetism || 0)}%`,
    `${dimensionLabel('stability')}: ${Number(dims.stability || 0)}%`,
    `${dimensionLabel('growth')}: ${Number(dims.growth || 0)}%`,
    localizedServerContent(translateSignsInText(result?.narrative?.text || ''), 'A solar-sign reading of the connection’s strongest dynamics.'),
  ].filter(Boolean).join('\n');
}

function scoreTone(score) {
  if (score >= 75) return synText('Alta Resonancia', 'High Resonance');
  if (score >= 58) return synText('Conexión Complementaria', 'Complementary Connection');
  if (score >= 44) return synText('Tensión Productiva', 'Productive Tension');
  return synText('Contraste Elemental', 'Elemental Contrast');
}

function useAnimatedNumber(target, duration = 1200) {
  const [value, setValue] = useState(0);

  useLayoutEffect(() => {
    const finalValue = Math.max(0, Number(target || 0));
    let rafId = 0;
    let startTime = 0;

    setValue(0);

    function frame(ts) {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(finalValue * eased));
      if (progress < 1) {
        rafId = window.requestAnimationFrame(frame);
      }
    }

    rafId = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(rafId);
  }, [target, duration]);

  return value;
}

function ScoreCardCopy({ score }) {
  const text = useMemo(() => {
    if (score >= 75) return synText('Conexión alta con energía armónica y gran potencial de atracción.', 'A highly resonant connection with harmonious energy and strong potential for attraction.');
    if (score >= 58) return synText('Compatibilidad interesante con una mezcla de apoyo, tensión y curiosidad mutua.', 'An intriguing match with a mix of support, tension, and mutual curiosity.');
    if (score >= 44) return synText('Hay fricción productiva: no es simple, pero puede mover mucho internamente.', 'There is productive tension: it is not simple, but it can spark meaningful inner growth.');
    return synText('Conexión desafiante, intensa y transformadora si ambas partes eligen sostenerla.', 'A challenging, intense, and transformative connection when both people choose to sustain it.');
  }, [score]);

  return <p style={styles.scoreSubcopy}>{text}</p>;
}

function DimensionBar({ label, value, delay = 0 }) {
  const [visibleWidth, setVisibleWidth] = useState(0);

  useEffect(() => {
    const width = Math.max(0, Math.min(100, Number(value || 0)));
    const timer = window.setTimeout(() => {
      setVisibleWidth(width);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return (
    <div style={styles.premiumBarCard}>
      <div style={styles.premiumBarHeader}>
        <div style={styles.premiumBarLabelWrap}>
          <span style={styles.premiumBarDot} />
          <span style={styles.premiumBarLabel}>{label}</span>
        </div>
        <span style={styles.premiumBarValue}>{value}%</span>
      </div>

      <div style={styles.premiumBarTrack}>
        <div style={{ ...styles.premiumBarFill, width: `${visibleWidth}%` }} />
      </div>
    </div>
  );
}

function PairSummary({ result, isMobile }) {
  if (!result?.pair || !result?.compatibility) return null;

  const leftGlyph = metaForSign(result.pair.signA).emoji;
  const rightGlyph = metaForSign(result.pair.signB).emoji;
  const leftSignName = labelForSign(result.pair.signA);
  const rightSignName = labelForSign(result.pair.signB);
  const leftElement = metaForSign(result.pair.signA).element;
  const rightElement = metaForSign(result.pair.signB).element;
  const compatibilityLabel = scoreTone(result.compatibility.masterScore);
  const compatibilitySummary = `${compatibilityLabel} · ${synText('una primera lectura basada en signo solar para abrir la puerta a una experiencia de sinastría más profunda.', 'an initial sun-sign reading that opens the door to a deeper synastry experience.')}`;

  return (
    <div style={{ ...styles.heroCard, ...styles.pairReveal }}>
      <div aria-hidden style={styles.heroGlowA} />
      <div aria-hidden style={styles.heroGlowB} />

      <div
        style={{
          ...styles.heroTopRow,
          ...(isMobile
            ? { gridTemplateColumns: '1fr', gap: 12 }
            : null),
        }}
      >
        <div style={styles.heroSignCol}>
          <div style={styles.heroGlyph}>{leftGlyph}</div>
          <div style={styles.heroSignName}>{leftSignName}</div>
          <div style={styles.heroElementRow}>
            <span>{leftElement}</span>
          </div>
        </div>

        <div
          style={{
            ...styles.heroSignCol,
            ...(isMobile ? { marginTop: 0 } : null),
          }}
        >
          <div style={styles.heroGlyph}>{rightGlyph}</div>
          <div style={styles.heroSignName}>{rightSignName}</div>
          <div style={styles.heroElementRow}>
            <span>{rightElement}</span>
          </div>
        </div>
      </div>

      <div style={styles.heroBottomBlock}>
        <span style={styles.heroBadge}>{compatibilityLabel}</span>
        <p style={styles.heroSummary}>{compatibilitySummary}</p>
      </div>
    </div>
  );
}

function ScorePanel({ result }) {
  const masterScore = Number(result?.compatibility?.masterScore || 0);
  const animatedScore = useAnimatedNumber(masterScore, 1250);

  if (!result?.compatibility || !result?.archetype) return null;

  return (
    <div style={styles.scoreCard}>
      <div style={styles.scoreGlow} />
      <div style={styles.shimmer} />
      <p style={styles.scoreLabel}>{synText('Compatibilidad Solar', 'Solar Compatibility')}</p>
      <div style={styles.scoreOrbWrap}>
  <div style={styles.scoreOrbRing} />
  <div style={styles.scoreValue}>{animatedScore}%</div>
</div>
      <ScoreCardCopy score={result.compatibility.masterScore} />

      <div style={styles.revealBadge}>{synText('✦ Resultado revelado', '✦ Result revealed')}</div>

      <div style={styles.archetypeBox}>
        <p style={styles.archetypeTitle}>
          {result.archetype.emoji} {localizedServerContent(result.archetype.label, scoreTone(result.compatibility.masterScore))}
        </p>
        <p style={styles.archetypeSubtitle}>
          {adaptElementPhrase(
            localizedServerContent(result.archetype.shortDescription, synText('', 'A concise reading of the connection’s core solar dynamic.')),
            result?.pair?.signA,
            result?.pair?.signB
          )}
        </p>
      </div>
    </div>
  );
}

function DimensionsCard({ dimensions }) {
  if (!dimensions || typeof dimensions !== 'object') return null;

  return (
    <div style={{ ...styles.dimensionsCard, ...styles.dimReveal }}>
      <div aria-hidden style={styles.dimensionsGlowA} />
      <div aria-hidden style={styles.dimensionsGlowB} />

      <div style={styles.dimensionsHeader}>
        <p style={styles.dimensionsEyebrow}>{synText('✦ Lectura dimensional', '✦ Dimensional reading')}</p>
        <h3 style={styles.dimensionsTitle}>{synText('Dimensiones de la conexión', 'Connection dimensions')}</h3>
      </div>

      <div style={styles.dimensionsList}>
        {Object.entries(dimensions).map(([key, value], index) => (
          <DimensionBar
            key={key}
            label={dimensionLabel(key)}
            value={value}
            delay={140 + index * 110}
          />
        ))}
      </div>
    </div>
  );
}

function MetricsRadar({ dimensions, insightText = '' }) {
  if (!dimensions) return null;

  const data = Object.entries(dimensions).map(([key, value]) => ({
    metric: dimensionLabel(key),
    value: Number(value || 0),
  }));

  const axes = buildRadarAxes(data);
  const polygonPoints = buildRadarPolygonPoints(data);

  return (
    <div style={{ ...styles.cardBlock }}>
      <h3 style={styles.sectionTitle}>{synText('Mapa energético del vínculo', 'Connection energy map')}</h3>
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <svg viewBox="0 0 360 360" width="100%" height="260" role="img" aria-label={synText('Mapa energético del vínculo', 'Connection energy map')}>
          {[30, 60, 90, 118].map((ring) => (
            <polygon
              key={`ring-${ring}`}
              points={buildRadarRingPoints(data.length, ring)}
              fill="none"
              stroke="rgba(218,165,32,0.15)"
              strokeWidth="1"
            />
          ))}

          {axes.map((axis) => (
            <g key={axis.key}>
              <line
                x1="180"
                y1="180"
                x2={axis.end.x}
                y2={axis.end.y}
                stroke="rgba(255,243,196,0.12)"
                strokeWidth="1"
              />
              <text
                x={axis.label.x}
                y={axis.label.y}
                fill="#FFF3C4"
                fontSize="12"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {axis.metric}
              </text>
            </g>
          ))}

          <polygon
            points={polygonPoints}
            fill="#DAA520"
            fillOpacity="0.35"
            stroke="#DAA520"
            strokeWidth="2.5"
          />

          <circle cx="180" cy="180" r="4" fill="#FFF3C4" opacity="0.9" />
        </svg>
      </div>
      {insightText ? (
        <p style={{ ...styles.narrativeText, marginTop: 10, fontSize: 14 }}>
          {insightText}
        </p>
      ) : null}
    </div>
  );
}

function NarrativeCard({ text }) {
  if (!text) return null;

  return (
    <div style={{ ...styles.interpretiveBlock, ...styles.narrativeReveal }}>
      <div aria-hidden style={styles.interpretiveGlowA} />
      <div aria-hidden style={styles.interpretiveGlowB} />

      <div style={styles.interpretiveHeader}>
        <p style={styles.interpretiveEyebrow}>{synText('✦ Insight relacional', '✦ Relationship insight')}</p>
        <h3 style={styles.interpretiveTitle}>{synText('Lectura interpretativa', 'Interpretive reading')}</h3>
      </div>

      <p style={styles.interpretiveText}>{localizedServerContent(translateSignsInText(text), 'This connection combines distinct strengths, tensions, and opportunities for mutual growth.')}</p>
    </div>
  );
}

function DisclosureCard({ message }) {
  if (!message) return null;

  return (
    <div style={styles.disclosureCard}>
      <strong style={{ color: '#FFF3C4' }}>Transparencia del sistema:</strong>{' '}
      {message}
    </div>
  );
}

export default function QuickResultCard({
  result,
  isMobile = false,
  onSaveQuickReading,
  onCompareQuickReading,
  savingQuickReading = false,
}) {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.getElementById(QUICK_RESULT_CARD_STYLE_ID)) return undefined;

    const style = document.createElement('style');
    style.id = QUICK_RESULT_CARD_STYLE_ID;
    style.textContent = QUICK_RESULT_CARD_STYLE_TEXT;
    document.head.appendChild(style);

    return undefined;
  }, []);

  const cardRef = useRef(null);
  const [sharePanelOpen, setSharePanelOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState('');

  const radarInsight = useMemo(() => {
    const dims = result?.compatibility?.dimensions || {};
    const ranked = Object.entries(dims)
      .map(([key, value]) => ({ label: dimensionLabel(key), value: Number(value || 0) }))
      .sort((a, b) => b.value - a.value);

    if (ranked.length < 2) return '';

    return synText(`La conexión concentra su mayor fuerza en ${ranked[0].label} y ${ranked[1].label}, lo que sugiere una base muy marcada en esa dinámica.`, `The connection is strongest in ${ranked[0].label} and ${ranked[1].label}, suggesting a clearly defined foundation in those dynamics.`);
  }, [result]);

  async function generateQuickCardBlob() {
    if (!result) return null;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bg.addColorStop(0, '#22043A');
      bg.addColorStop(0.22, '#2E0A4F');
      bg.addColorStop(0.5, '#3B0F63');
      bg.addColorStop(0.78, '#4B0082');
      bg.addColorStop(1, '#DAA520');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const shellX = 56;
      const shellY = 72;
      const shellW = canvas.width - 112;
      const shellH = canvas.height - 144;
      drawRoundedRect(ctx, shellX, shellY, shellW, shellH, 34);
      ctx.fillStyle = 'rgba(16,0,36,0.72)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(218,165,32,0.36)';
      ctx.lineWidth = 2;
      ctx.stroke();

      const left = shellX + 54;
      const contentW = shellW - 108;
      let y = shellY + 62;

      const signA = labelForSign(result?.pair?.signA);
      const signB = labelForSign(result?.pair?.signB);
      const dims = result?.compatibility?.dimensions || {};

      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 26px Arial';
      ctx.fillText('✦ COMPATIBILIDAD SOLAR', left, y);

      y += 44;
      ctx.fillStyle = '#FFF4CC';
      ctx.font = '900 58px Arial';
      ctx.fillText('Zodiacus', left, y);

      y += 52;
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 34px Arial';
      ctx.fillText(truncateCanvasText(ctx, `${signA} ❤ ${signB}`, contentW), left, y);

      y += 48;
      ctx.fillStyle = 'rgba(255,255,255,0.78)';
      ctx.font = '500 24px Arial';
      const subLines = wrapCanvasText(ctx, scoreTone(result?.compatibility?.masterScore), contentW);
      subLines.slice(0, 2).forEach((line) => {
        ctx.fillText(line, left, y);
        y += 30;
      });

      y += 20;
      drawRoundedRect(ctx, left, y, contentW, 170, 26);
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.10)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 22px Arial';
      ctx.fillText('COMPATIBILIDAD SOLAR', left + 24, y + 38);
      ctx.fillStyle = '#FFF4CC';
      ctx.font = '900 86px Arial';
      ctx.fillText(`${Number(result?.compatibility?.masterScore || 0)}%`, left + 24, y + 126);

      y += 210;
      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 24px Arial';
      ctx.fillText('ARQUETIPO DEL VÍNCULO', left, y);
      y += 40;
      ctx.fillStyle = '#FFF4CC';
      ctx.font = '900 34px Arial';
      ctx.fillText(truncateCanvasText(ctx, `${result?.archetype?.emoji || '✦'} ${result?.archetype?.label || ''}`, contentW), left, y);
      y += 38;
      ctx.fillStyle = 'rgba(255,255,255,0.82)';
      ctx.font = '500 24px Arial';
      wrapCanvasText(ctx, adaptElementPhrase(result?.archetype?.shortDescription || '', result?.pair?.signA, result?.pair?.signB), contentW)
        .slice(0, 2)
        .forEach((line) => {
          ctx.fillText(line, left, y);
          y += 30;
        });

      y += 20;
      ctx.fillStyle = '#F7D26A';
      ctx.font = '900 24px Arial';
      ctx.fillText('DIMENSIONES', left, y);
      y += 22;

      [
        ['Energía', Number(dims.energy || 0)],
        ['Expresión', Number(dims.expression || 0)],
        ['Magnetismo', Number(dims.magnetism || 0)],
        ['Estabilidad', Number(dims.stability || 0)],
        ['Crecimiento', Number(dims.growth || 0)],
      ].forEach(([label, value], index) => {
        const rowY = y + index * 72;
        ctx.fillStyle = '#FFF4CC';
        ctx.font = '800 24px Arial';
        ctx.fillText(label, left, rowY + 24);
        ctx.fillStyle = '#F7D26A';
        ctx.font = '900 24px Arial';
        ctx.fillText(`${value}%`, left + contentW - 88, rowY + 24);
        drawRoundedRect(ctx, left, rowY + 34, contentW, 18, 999);
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        ctx.fill();
        drawRoundedRect(ctx, left, rowY + 34, Math.max(12, (contentW * value) / 100), 18, 999);
        ctx.fillStyle = '#DAA520';
        ctx.fill();
      });

      y += 5 * 72 + 24;
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      ctx.font = '700 24px Arial';
      const narrativeLines = wrapCanvasText(ctx, translateSignsInText(result?.narrative?.text || ''), contentW);
      narrativeLines.slice(0, 5).forEach((line, index) => {
        ctx.fillText(line, left, y + index * 32);
      });

      ctx.fillStyle = 'rgba(255,255,255,0.62)';
      ctx.font = '700 18px Arial';
      ctx.fillText('zodiacus.app · Compatibilidad solar premium', left, shellY + shellH - 24);

      return await canvasToBlob(canvas);
    } catch {
      return null;
    }
  }

  async function handleDownloadCard() {
    const blob = await generateQuickCardBlob();
    if (!blob) {
      setShareStatus(synText('No se pudo generar la card de compatibilidad.', 'The compatibility card could not be generated.'));
      return;
    }
    downloadBlobFile(blob, 'zodiacus-compatibilidad-solar.png');
    setShareStatus(synText('Card descargada con éxito.', 'Card downloaded successfully.'));
  }

  function handleToggleSharePanel() {
    setShareStatus('');
    setSharePanelOpen((prev) => !prev);
  }

  function handleShareWhatsApp() {
    const summary = buildQuickShareSummary(result);
    openSharePopup(`https://wa.me/?text=${encodeURIComponent(summary)}`, 'share-whatsapp-quick');
  }

  function handleShareFacebook() {
    openSharePopup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://zodiacus.app')}`, 'share-facebook-quick');
    setShareStatus(synText('Facebook compartirá el enlace. Si querés la imagen, usá “Descargar archivo”.', 'Facebook will share the link. To use the image, choose “Download file”.'));
  }

  function handleShareTelegram() {
    const summary = buildQuickShareSummary(result);
    openSharePopup(`https://t.me/share/url?url=${encodeURIComponent('https://zodiacus.app')}&text=${encodeURIComponent(summary)}`, 'share-telegram-quick');
  }

  function handleShareX() {
    const summary = buildQuickShareSummary(result);
    openSharePopup(`https://twitter.com/intent/tweet?text=${encodeURIComponent(summary)}`, 'share-x-quick');
  }

  async function handleShareInstagram() {
    setShareStatus(synText('Instagram se abre en una ventana emergente, pero la imagen no se puede precargar desde la web. Usá “Descargar archivo” y luego subila como historia o publicación.', 'Instagram opens in a pop-up, but the image cannot be preloaded from the web. Download the file, then upload it as a story or post.'));
    openSharePopup('https://www.instagram.com/', 'share-instagram-quick');
  }

  async function handleCopySummary() {
    const summary = buildQuickShareSummary(result);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(summary);
        setShareStatus(synText('Resumen copiado al portapapeles.', 'Summary copied to the clipboard.'));
        return;
      }
      setShareStatus(synText('No fue posible copiar el resumen desde este dispositivo.', 'The summary could not be copied on this device.'));
    } catch {
      setShareStatus(synText('No se pudo copiar el resumen.', 'The summary could not be copied.'));
    }
  }

  async function handleSaveQuickReading() {
    if (typeof onSaveQuickReading !== 'function') {
      setShareStatus(synText('La acción de guardar todavía no está conectada en esta pantalla.', 'Saving is not available on this screen yet.'));
      return;
    }

    try {
      await onSaveQuickReading(result);
      setShareStatus(synText('Lectura solar guardada en la Biblioteca Cósmica.', 'Solar reading saved to the Cosmic Library.'));
    } catch (err) {
      if (err?.code === 'LIBRARY_LIMIT_REACHED') {
        setShareStatus(synText('Tu biblioteca está llena. Eliminá una lectura guardada para poder guardar una nueva.', 'Your library is full. Delete a saved reading before saving a new one.'));
        return;
      }

      setShareStatus(localizedServerContent(err?.message, 'The solar reading could not be saved.'));
    }
  }

  if (!result?.pair || !result?.compatibility) return null;

  // Handler for comparing quick readings (Quick-only)
  async function handleCompareQuickReading() {
    if (typeof onCompareQuickReading !== 'function') {
      setShareStatus(synText('La comparación solar todavía no está conectada en esta pantalla.', 'Solar comparison is not available on this screen yet.'));
      return;
    }

    setShareStatus('');

    try {
      await Promise.resolve(onCompareQuickReading(result));
    } catch (error) {
      const rawMessage = String(error?.message || '').trim();
      const message = rawMessage.toLowerCase();

      if (
        message.includes('otra lectura solar guardada') ||
        message.includes('otra lectura quick guardada') ||
        (message.includes('lectura solar') && message.includes('guardada')) ||
        (message.includes('lectura quick') && message.includes('guardada')) ||
        (message.includes('compatibilidad solar') && message.includes('comparar'))
      ) {
        setShareStatus(
          localizedServerContent(rawMessage, 'You need at least one other saved solar reading to compare this compatibility.')
        );
        return;
      }

      if (rawMessage) {
        setShareStatus(localizedServerContent(rawMessage, 'The saved readings could not be compared.'));
        return;
      }

      setShareStatus(synText('No se pudo abrir la comparación de lecturas solares.', 'The solar-reading comparison could not be opened.'));
    }
  }

  return (
    <div style={styles.resultCard}>
      <div style={isMobile ? styles.mobileTop : styles.desktopTop}>
        <PairSummary result={result} isMobile={isMobile} />
        <ScorePanel result={result} />
      </div>

      <div style={styles.bottomGrid}>
        <MetricsRadar dimensions={result?.compatibility?.dimensions} insightText={radarInsight} />
        <DimensionsCard dimensions={result?.compatibility?.dimensions} />
        <NarrativeCard text={result?.narrative?.text} />
        <DisclosureCard message={result?.disclosure?.message} />
      </div>
      <div ref={cardRef} style={styles.actionRow}>
        <button
          type="button"
          onClick={handleToggleSharePanel}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.10)';
            e.currentTarget.style.filter = 'brightness(1.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
            e.currentTarget.style.filter = 'none';
          }}
          style={styles.actionButton}
        >
          {synText('Descargar / compartir card', 'Download / share card')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/saved-synastries')}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 24px 52px rgba(0,0,0,0.38), 0 0 28px rgba(247,215,116,0.30), 0 0 16px rgba(138,43,226,0.18), inset 0 1px 0 rgba(255,255,255,0.10)';
            e.currentTarget.style.filter = 'brightness(1.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 18px 44px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.08)';
            e.currentTarget.style.filter = 'none';
          }}
          style={styles.secondaryActionButton}
        >
          {synText('✦ Biblioteca Cósmica', '✦ Cosmic Library')}
        </button>
        <button
          type="button"
          onClick={handleSaveQuickReading}
          disabled={savingQuickReading}
          onMouseEnter={(e) => {
            if (savingQuickReading) return;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.10)';
            e.currentTarget.style.filter = 'brightness(1.03)';
          }}
          onMouseLeave={(e) => {
            if (savingQuickReading) return;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
            e.currentTarget.style.filter = 'none';
          }}
          style={{
            ...styles.actionButton,
            ...(savingQuickReading ? styles.actionButtonDisabled : null),
          }}
        >
          {savingQuickReading ? 'Guardando…' : 'Guardar en biblioteca'}
        </button>
        <button
          type="button"
          onClick={handleCompareQuickReading}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.24), inset 0 1px 0 rgba(255,255,255,0.10)';
            e.currentTarget.style.filter = 'brightness(1.03)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
            e.currentTarget.style.filter = 'none';
          }}
          style={styles.actionButton}
        >
          Comparar lectura solar
        </button>
      </div>

      {shareStatus ? <div style={styles.shareStatus}>{shareStatus}</div> : null}

      {sharePanelOpen ? (
        <div style={styles.sharePanel}>
          <button
            type="button"
            onClick={handleShareWhatsApp}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            WhatsApp
          </button>
          <button
            type="button"
            onClick={handleShareInstagram}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            Instagram
          </button>
          <button
            type="button"
            onClick={handleShareFacebook}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            Facebook
          </button>
          <button
            type="button"
            onClick={handleShareTelegram}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            Telegram
          </button>
          <button
            type="button"
            onClick={handleShareX}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            X / Twitter
          </button>
          <button
            type="button"
            onClick={handleCopySummary}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            Copiar resumen
          </button>
          <button
            type="button"
            onClick={handleDownloadCard}
            style={styles.actionButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 22px 48px rgba(0,0,0,0.34), 0 0 24px rgba(247,215,116,0.22), inset 0 1px 0 rgba(255,255,255,0.10)';
              e.currentTarget.style.filter = 'brightness(1.03)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 16px 38px rgba(0,0,0,0.28), 0 0 20px rgba(247,215,116,0.18), inset 0 1px 0 rgba(255,255,255,0.08)';
              e.currentTarget.style.filter = 'none';
            }}
          >
            Descargar archivo
          </button>
        </div>
      ) : null}
    </div>
  );
}
